import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api'
import type { Category, Client, Todo } from '../../shared/types'
import { formatHours, parseDuration } from '../../shared/duration'
import { mmddyy } from '../datetime'
import { pushError, pushToast } from '../toast'
import CategoryCombo from './CategoryCombo'
import ClientCombo from './ClientCombo'
import { useColumns, ColGroup, ColResizer, ColumnsMenu, WrapText, type ColumnDef } from '../columns'
import TodoCalendar from './TodoCalendar'
import LogTodoModal from './LogTodoModal'

type SortKey = 'urgency' | 'impact' | 'priority' | 'order'
const priority = (t: Todo): number | null => (t.urgency != null && t.impact != null ? t.urgency + t.impact : null)

interface Row extends Todo {
  _key: number
}
const DEFS: ColumnDef[] = [
  { key: 'client', label: 'Client', width: 180 },
  { key: 'task', label: 'Task', width: 300 },
  { key: 'category', label: 'Category', width: 170 },
  { key: 'est', label: 'Est', width: 72 },
  { key: 'urgency', label: 'Urg', width: 64 },
  { key: 'impact', label: 'Imp', width: 64 },
  { key: 'priority', label: 'Prio', width: 64 },
  { key: 'sort_order', label: 'Order', width: 72 },
  { key: 'due', label: 'Due', width: 104 },
  { key: 'actions', label: '', width: 84, fixed: true },
]
// Keyboard-navigable columns (Priority computed, Due/actions are click affordances).
const NAV_ALL = ['client', 'task', 'category', 'est', 'urgency', 'impact', 'sort_order']
const TEXT_COLS = new Set(['task', 'est', 'sort_order'])
const SORTABLE: Record<string, SortKey> = { urgency: 'urgency', impact: 'impact', priority: 'priority', sort_order: 'order' }

function hhmmToMin(v: string): number {
  const [h, m] = v.split(':').map(Number)
  return h * 60 + (m || 0)
}
function minToHHMM(min: number): string {
  const m = ((Math.round(min) % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

// Sort real rows per the active sort; blank row is appended by the caller.
function sortRows(rows: Row[], sort: { key: SortKey; dir: 'asc' | 'desc' } | null): Row[] {
  const list = [...rows]
  if (!sort) {
    // default: ordered rows first (asc), then unordered by priority desc
    list.sort((a, b) => {
      const ao = a.sort_order,
        bo = b.sort_order
      const pa = priority(a) ?? -Infinity,
        pb = priority(b) ?? -Infinity
      if (ao != null && bo != null) return ao - bo || pb - pa
      if (ao != null) return -1
      if (bo != null) return 1
      return pb - pa
    })
    return list
  }
  const dir = sort.dir === 'asc' ? 1 : -1
  const val = (t: Todo) =>
    sort.key === 'priority' ? priority(t) ?? -Infinity : sort.key === 'order' ? t.sort_order ?? Infinity : (t as any)[sort.key] ?? -Infinity
  list.sort((a, b) => (val(a) - val(b)) * dir)
  return list
}

export default function Todos({ initialClientId }: { initialClientId?: number }) {
  const [clients, setClients] = useState<Client[]>([])
  const [allCats, setAllCats] = useState<Category[]>([])
  const [rows, setRows] = useState<Row[]>([])
  const [filterClient, setFilterClient] = useState<number | 'all'>(initialClientId ?? 'all')
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null)
  const [logging, setLogging] = useState<Row | null>(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'list' | 'calendar'>('list')
  const [archiveOpen, setArchiveOpen] = useState(false)

  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const keyCounter = useRef(1)
  const nextKey = () => keyCounter.current++

  const makeBlank = useCallback(
    (): Row => ({
      _key: nextKey(),
      id: 0,
      client_id: filterClient === 'all' ? null : filterClient,
      task: '',
      category_id: null,
      est_minutes: null,
      due_date: null,
      start_time: null,
      urgency: null,
      impact: null,
      sort_order: null,
      done: 0,
      created_at: '',
      updated_at: '',
    }),
    [filterClient],
  )

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [cl, cats, ts] = await Promise.all([
        api.listClients(false),
        api.listAllCategories(),
        api.listTodos(filterClient === 'all' ? undefined : filterClient),
      ])
      setClients(cl)
      setAllCats(cats)
      setRows([...sortRows(ts.map((t) => ({ ...t, _key: nextKey() })), null), makeBlank()])
    } catch (e) {
      pushError(`Couldn't load to-dos: ${(e as Error).message}`)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterClient])

  useEffect(() => {
    load()
  }, [load])

  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])

  function applySort(key: SortKey) {
    const next: { key: SortKey; dir: 'asc' | 'desc' } =
      sort?.key === key ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'priority' ? 'desc' : 'asc' }
    setSort(next)
    setRows((rs) => {
      const real = rs.filter((r) => r.id !== 0)
      const blank = rs.find((r) => r.id === 0) ?? makeBlank()
      return [...sortRows(real, next), blank]
    })
  }

  // ── saving (debounced updates; create on first edit of the blank row) ──
  const pending = useRef<Map<number, Partial<Todo>>>(new Map())
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())
  const flush = useCallback((id: number) => {
    const p = pending.current.get(id)
    if (!p) return
    pending.current.delete(id)
    api.updateTodo(id, p).catch((e) => pushError(`Couldn't save: ${(e as Error).message}`))
  }, [])
  const scheduleSave = useCallback(
    (id: number, patch: Partial<Todo>) => {
      pending.current.set(id, { ...(pending.current.get(id) ?? {}), ...patch })
      const t = timers.current.get(id)
      if (t) clearTimeout(t)
      timers.current.set(id, setTimeout(() => flush(id), 400))
    },
    [flush],
  )
  const flushAll = useCallback(() => {
    for (const id of [...pending.current.keys()]) flush(id)
  }, [flush])
  useEffect(() => () => flushAll(), [flushAll])

  const setLocal = useCallback((key: number, patch: Partial<Row>) => {
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)))
  }, [])

  const creating = useRef<Set<number>>(new Set())
  const hasContent = (r: Row) =>
    r.client_id != null || (r.task && r.task.trim() !== '') || r.est_minutes != null || r.urgency != null || r.impact != null || r.sort_order != null || r.due_date != null || r.category_id != null

  const createRow = useCallback(
    async (key: number, seed: Row) => {
      if (creating.current.has(key)) return
      if (!hasContent(seed)) return
      creating.current.add(key)
      try {
        const created = await api.createTodo({
          client_id: seed.client_id,
          task: seed.task,
          category_id: seed.category_id,
          est_minutes: seed.est_minutes,
          due_date: seed.due_date,
          urgency: seed.urgency,
          impact: seed.impact,
          sort_order: seed.sort_order,
        })
        setRows((rs) => {
          const mapped = rs.map((r) => (r._key === key ? { ...r, id: created.id } : r))
          if (!mapped.some((r) => r.id === 0)) mapped.push(makeBlank())
          return mapped
        })
      } catch (e) {
        pushError(`Couldn't add to-do: ${(e as Error).message}`)
      } finally {
        creating.current.delete(key)
      }
    },
    [makeBlank],
  )

  const commit = useCallback(
    (row: Row, patch: Partial<Row>) => {
      setLocal(row._key, patch)
      const cur = rowsRef.current.find((r) => r._key === row._key) ?? row
      const merged = { ...cur, ...patch } as Row
      if (merged.id === 0) void createRow(row._key, merged)
      else scheduleSave(merged.id, patch)
    },
    [createRow, scheduleSave, setLocal],
  )

  async function removeRow(row: Row) {
    if (row.id === 0) return
    setRows((rs) => rs.filter((r) => r._key !== row._key))
    try {
      await api.deleteTodo(row.id)
    } catch (e) {
      pushError(`Delete failed: ${(e as Error).message}`)
    }
  }

  // ── columns + keyboard navigation (key-based, skips hidden columns) ──
  const colsApi = useColumns('todos', DEFS)
  const navKeys = NAV_ALL.filter((k) => colsApi.isVisible(k))
  const refs = useRef<Map<string, HTMLElement>>(new Map())
  const refKey = (k: number, col: string) => `${k}|${col}`
  const register = (k: number, col: string) => (el: HTMLElement | null) => {
    const key = refKey(k, col)
    if (el) refs.current.set(key, el)
    else refs.current.delete(key)
  }
  const focusKey = useCallback((rowIdx: number, colKey: string, caret?: 'start' | 'end') => {
    const rs = rowsRef.current
    if (rowIdx < 0 || rowIdx >= rs.length || !colKey) return
    const el = refs.current.get(refKey(rs[rowIdx]._key, colKey))
    if (!el) return
    el.focus()
    if (el instanceof HTMLInputElement && caret) {
      const n = caret === 'end' ? el.value.length : 0
      try {
        el.setSelectionRange(n, n)
      } catch {
        /* ignore */
      }
    }
  }, [])

  const navKeyDown = useCallback(
    (e: React.KeyboardEvent, rowIdx: number, colKey: string) => {
      const el = e.currentTarget as HTMLElement
      const isText = TEXT_COLS.has(colKey) && el instanceof HTMLInputElement
      const idx = navKeys.indexOf(colKey)
      const mod = e.ctrlKey || e.metaKey
      if (mod && (e.key === 'Backspace' || e.key === 'Delete')) {
        e.preventDefault()
        removeRow(rowsRef.current[rowIdx])
        return
      }
      switch (e.key) {
        case 'Enter':
          e.preventDefault()
          flushAll()
          focusKey(e.shiftKey ? rowIdx - 1 : rowIdx + 1, colKey, 'end')
          break
        case 'Tab':
          e.preventDefault()
          if (e.shiftKey) {
            if (idx > 0) focusKey(rowIdx, navKeys[idx - 1], 'end')
            else focusKey(rowIdx - 1, navKeys[navKeys.length - 1], 'end')
          } else {
            if (idx < navKeys.length - 1) focusKey(rowIdx, navKeys[idx + 1], 'end')
            else focusKey(rowIdx + 1, navKeys[0], 'end')
          }
          break
        case 'ArrowDown':
          e.preventDefault()
          focusKey(rowIdx + 1, colKey, 'end')
          break
        case 'ArrowUp':
          e.preventDefault()
          focusKey(rowIdx - 1, colKey, 'end')
          break
        case 'ArrowLeft':
          if (!isText || ((el as HTMLInputElement).selectionStart === 0 && (el as HTMLInputElement).selectionEnd === 0)) {
            e.preventDefault()
            if (idx > 0) focusKey(rowIdx, navKeys[idx - 1], 'end')
          }
          break
        case 'ArrowRight':
          if (!isText || ((el as HTMLInputElement).selectionStart === (el as HTMLInputElement).value.length && (el as HTMLInputElement).selectionEnd === (el as HTMLInputElement).value.length)) {
            e.preventDefault()
            if (idx < navKeys.length - 1) focusKey(rowIdx, navKeys[idx + 1], 'start')
          }
          break
      }
    },
    [navKeys, flushAll, focusKey],
  )

  function renderCell(colKey: string, t: Row, rowIdx: number) {
    const isBlank = t.id === 0
    const onKey = (e: React.KeyboardEvent) => navKeyDown(e, rowIdx, colKey)
    switch (colKey) {
      case 'client': {
        const accent = t.client_id != null ? clientById.get(t.client_id)?.accent_color ?? '#94a3b8' : '#cbd5e1'
        return (
          <td key="client" className="border-r border-slate-100" style={{ borderLeftColor: accent, borderLeftWidth: 3 }}>
            <ClientCombo value={t.client_id} clients={clients} onCommit={(id) => commit(t, { client_id: id, category_id: null })} gridKeyDown={onKey} registerRef={register(t._key, 'client')} />
          </td>
        )
      }
      case 'task':
        return (
          <td key="task" className="border-r border-slate-100">
            <WrapText wrap={colsApi.wrap} value={t.task} placeholder={isBlank ? 'New task…' : ''} onChange={(v) => commit(t, { task: v })} onKeyDown={onKey} registerRef={register(t._key, 'task')} />
          </td>
        )
      case 'category': {
        const cats = t.client_id != null ? allCats.filter((c) => c.client_id === t.client_id) : []
        return (
          <td key="category" className="border-r border-slate-100">
            <CategoryCombo value={t.category_id} categories={cats} disabled={t.client_id == null} onCommit={(id) => commit(t, { category_id: id })} gridKeyDown={onKey} registerRef={register(t._key, 'category')} />
          </td>
        )
      }
      case 'est':
        return (
          <td key="est" className="border-r border-slate-100">
            <EstInput minutes={t.est_minutes} registerRef={register(t._key, 'est')} onKeyDown={onKey} onCommit={(m) => commit(t, { est_minutes: m })} />
          </td>
        )
      case 'urgency':
        return (
          <td key="urgency" className="border-r border-slate-100 text-right">
            <ScaleSelect value={t.urgency} registerRef={register(t._key, 'urgency')} onKeyDown={onKey} onChange={(v) => commit(t, { urgency: v })} />
          </td>
        )
      case 'impact':
        return (
          <td key="impact" className="border-r border-slate-100 text-right">
            <ScaleSelect value={t.impact} registerRef={register(t._key, 'impact')} onKeyDown={onKey} onChange={(v) => commit(t, { impact: v })} />
          </td>
        )
      case 'priority':
        return (
          <td key="priority" className="border-r border-slate-100 px-2 py-1 text-right tabular font-semibold text-slate-800">
            {priority(t) ?? ''}
          </td>
        )
      case 'sort_order':
        return (
          <td key="sort_order" className="border-r border-slate-100">
            <input
              ref={register(t._key, 'sort_order') as (el: HTMLInputElement | null) => void}
              className="grid-cell w-full bg-transparent px-2 py-1 text-right tabular outline-none"
              inputMode="numeric"
              value={t.sort_order ?? ''}
              placeholder="—"
              onChange={(e) => {
                const v = e.target.value.trim()
                commit(t, { sort_order: v === '' ? null : Number(v) })
              }}
              onKeyDown={onKey}
            />
          </td>
        )
      case 'due':
        return (
          <td key="due" className="border-r border-slate-100 text-center">
            <DueDateCell value={t.due_date} onChange={(d) => commit(t, { due_date: d })} />
          </td>
        )
      case 'actions':
        return (
          <td key="actions" className="whitespace-nowrap px-2 py-1 text-right">
            {!isBlank && (
              <>
                <button className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 hover:bg-green-200" tabIndex={-1} onClick={() => setLogging(t)}>
                  Done
                </button>
                <button className="ml-1 text-slate-300 hover:text-red-500" tabIndex={-1} title="Delete" onClick={() => removeRow(t)}>
                  ✕
                </button>
              </>
            )}
          </td>
        )
      default:
        return null
    }
  }

  async function completeTodo(
    t: Row,
    logEntry: boolean,
    entryPatch?: { client_id: number | null; description: string; category_id: number | null; entry_date: string; duration_minutes: number; start_time: string | null },
  ) {
    try {
      if (logEntry && entryPatch && entryPatch.client_id != null) {
        const body: Record<string, unknown> = {
          client_id: entryPatch.client_id,
          entry_date: entryPatch.entry_date,
          description: entryPatch.description,
          category_id: entryPatch.category_id,
          duration_minutes: entryPatch.duration_minutes,
        }
        if (entryPatch.start_time) {
          body.start_time = entryPatch.start_time
          body.end_time = minToHHMM(hhmmToMin(entryPatch.start_time) + entryPatch.duration_minutes)
        }
        await api.createEntry(body as any)
      }
      if (t.id !== 0) await api.updateTodo(t.id, { done: 1 })
      setRows((rs) => rs.filter((x) => x._key !== t._key))
      setLogging(null)
      pushToast(logEntry ? 'Logged and marked done' : 'Marked done')
    } catch (e) {
      pushError(`Couldn't complete: ${(e as Error).message}`)
    }
  }

  const alignFor = (key: string) => (key === 'due' ? 'text-center' : SORTABLE[key] || key === 'est' ? 'text-right' : 'text-left')

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6 lg:px-8">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">To-Do</h1>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-slate-300 text-sm">
            {(['list', 'calendar'] as const).map((v) => (
              <button
                key={v}
                className={'px-3 py-1.5 capitalize ' + (view === v ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100')}
                onClick={() => setView(v)}
              >
                {v}
              </button>
            ))}
          </div>
          <select
            className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800"
            value={filterClient === 'all' ? 'all' : String(filterClient)}
            onChange={(e) => setFilterClient(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          >
            <option value="all">All clients</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.simple_name}
              </option>
            ))}
          </select>
          {view === 'list' && <ColumnsMenu cols={colsApi} />}
          <button
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title="Completed / archived to-dos"
            aria-label="Archived to-dos"
            onClick={() => setArchiveOpen(true)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="1.5" y="2.5" width="13" height="3" rx="0.5" />
              <path d="M2.5 5.5v7a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-7" />
              <line x1="6" y1="8.5" x2="10" y2="8.5" />
            </svg>
          </button>
        </div>
      </header>

      {view === 'calendar' ? (
        <TodoCalendar filterClient={filterClient} onChanged={load} />
      ) : loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="border-collapse text-sm" style={{ tableLayout: 'fixed', width: colsApi.totalWidth }}>
            <ColGroup cols={colsApi} />
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {colsApi.visible.map((d) => {
                  if (d.key === 'actions') return <th key="actions" className="px-2 py-2" />
                  const sortK = SORTABLE[d.key]
                  return (
                    <th key={d.key} className={'relative px-2 py-2 ' + alignFor(d.key)}>
                      {sortK ? (
                        <button className="inline-flex items-center gap-1 hover:text-slate-800" onClick={() => applySort(sortK)}>
                          {d.label}
                          {sort?.key === sortK && <span>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </button>
                      ) : (
                        d.label
                      )}
                      <ColResizer cols={colsApi} colKey={d.key} />
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((t, rowIdx) => (
                <tr key={t._key} className="border-b border-slate-100 align-middle hover:bg-slate-50/40">
                  {colsApi.visible.map((d) => renderCell(d.key, t, rowIdx))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {logging && (
        <LogTodoModal
          todo={logging}
          clients={clients}
          allCats={allCats}
          onCancel={() => setLogging(null)}
          onLog={(p) => completeTodo(logging, true, p)}
          onDontLog={() => completeTodo(logging, false)}
        />
      )}

      {archiveOpen && (
        <ArchiveModal clients={clients} allCats={allCats} filterClient={filterClient} onRestored={load} onClose={() => setArchiveOpen(false)} />
      )}
    </div>
  )
}

function ArchiveModal({
  clients,
  allCats,
  filterClient,
  onRestored,
  onClose,
}: {
  clients: Client[]
  allCats: Category[]
  filterClient: number | 'all'
  onRestored: () => void
  onClose: () => void
}) {
  const [items, setItems] = useState<Todo[]>([])
  const [loading, setLoading] = useState(true)
  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients])

  useEffect(() => {
    ;(async () => {
      try {
        const rows = await api.listArchivedTodos(filterClient === 'all' ? undefined : filterClient)
        rows.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
        setItems(rows)
      } catch (e) {
        pushError(`Couldn't load archive: ${(e as Error).message}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [filterClient])

  async function restore(t: Todo) {
    setItems((is) => is.filter((x) => x.id !== t.id))
    try {
      await api.updateTodo(t.id, { done: 0 })
      onRestored()
    } catch (e) {
      pushError(`Couldn't restore: ${(e as Error).message}`)
    }
  }
  async function remove(t: Todo) {
    setItems((is) => is.filter((x) => x.id !== t.id))
    try {
      await api.deleteTodo(t.id)
    } catch (e) {
      pushError(`Delete failed: ${(e as Error).message}`)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-auto bg-black/40 p-4 sm:p-6">
      <div className="mt-8 w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-lg font-semibold text-slate-900">Completed to-dos</h2>
          <button className="text-slate-400 hover:text-slate-700" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto p-2">
          {loading ? (
            <p className="p-6 text-center text-slate-400">Loading…</p>
          ) : items.length === 0 ? (
            <p className="p-8 text-center text-slate-400">No completed to-dos yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((t) => {
                const accent = t.client_id != null ? clientById.get(t.client_id)?.accent_color ?? '#94a3b8' : '#cbd5e1'
                const cat = t.category_id != null ? allCats.find((c) => c.id === t.category_id) : undefined
                return (
                  <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: accent }} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-slate-700 line-through">{t.task || '(untitled)'}</span>
                      <span className="block truncate text-xs text-slate-400">
                        {t.client_id != null ? clientById.get(t.client_id)?.simple_name ?? '' : 'No client'}
                        {cat ? ` · ${cat.name}` : ''}
                      </span>
                    </span>
                    <button className="shrink-0 text-xs font-medium text-[var(--accent)] hover:underline" onClick={() => restore(t)}>
                      Restore
                    </button>
                    <button className="shrink-0 text-slate-300 hover:text-red-500" title="Delete permanently" onClick={() => remove(t)}>
                      ✕
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

function ScaleSelect({
  value,
  onChange,
  onKeyDown,
  registerRef,
}: {
  value: number | null
  onChange: (v: number | null) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  registerRef: (el: HTMLElement | null) => void
}) {
  return (
    <select
      ref={registerRef as (el: HTMLSelectElement | null) => void}
      className="bg-transparent px-1 py-1 text-right tabular text-sm outline-none"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      onKeyDown={onKeyDown}
    >
      <option value="">—</option>
      {[1, 2, 3, 4, 5].map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  )
}

function EstInput({
  minutes,
  onCommit,
  onKeyDown,
  registerRef,
}: {
  minutes: number | null
  onCommit: (m: number | null) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  registerRef: (el: HTMLElement | null) => void
}) {
  const [text, setText] = useState(() => (minutes ? formatHours(minutes) : ''))
  const [editing, setEditing] = useState(false)
  useEffect(() => {
    if (!editing) setText(minutes ? formatHours(minutes) : '')
  }, [minutes, editing])
  function done() {
    setEditing(false)
    const trimmed = text.trim()
    if (trimmed === '') {
      if (minutes != null) onCommit(null)
      else setText('')
      return
    }
    const p = parseDuration(trimmed)
    if (p == null || p === minutes) setText(minutes ? formatHours(minutes) : '')
    else onCommit(p)
  }
  return (
    <input
      ref={registerRef as (el: HTMLInputElement | null) => void}
      className="grid-cell w-full bg-transparent px-2 py-1 text-right tabular outline-none"
      value={text}
      placeholder="—"
      onFocus={() => setEditing(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={done}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Tab') done()
        onKeyDown(e)
      }}
    />
  )
}

function fmtDue(iso: string): string {
  return mmddyy(iso)
}
function DueDateCell({ value, onChange }: { value: string | null; onChange: (d: string | null) => void }) {
  const ref = useRef<HTMLInputElement>(null)
  const open = () => {
    const el = ref.current
    if (!el) return
    try {
      el.showPicker()
    } catch {
      el.focus()
    }
  }
  return (
    <div className="relative flex items-center justify-center gap-1">
      {value ? (
        <>
          <button type="button" tabIndex={-1} className="tabular text-xs font-medium text-slate-700 hover:underline" title={`Due ${value}`} onClick={open}>
            {fmtDue(value)}
          </button>
          <button type="button" tabIndex={-1} className="text-xs text-slate-300 hover:text-red-500" title="Clear due date" onClick={() => onChange(null)}>
            ✕
          </button>
        </>
      ) : (
        <button type="button" tabIndex={-1} className="text-slate-300 hover:text-slate-600" title="Set due date" onClick={open}>
          📅
        </button>
      )}
      <input ref={ref} type="date" className="pointer-events-none absolute h-0 w-0 opacity-0" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} />
    </div>
  )
}
